/**
 * WPS Pages Editor Functions
 * Handles loading and saving content with AlpineBlocks
 */

export class WpsPageEditor {
  constructor() {
    this.currentPageId = null;
    this.isDirty = false;
  }

  /**
   * Initialize the editor with existing content
   */
  async initializeEditor(pageId, content) {
    this.currentPageId = pageId;
    
    // Wait for AlpineBlocks to be ready
    await this.waitForAlpineBlocks();
    
    if (content) {
      await this.loadContent(content);
    }
    
    // Set up autosave if needed
    this.setupAutosave();
  }

  /**
   * Wait for AlpineBlocks to be fully initialized
   */
  async waitForAlpineBlocks() {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (window.Alpine && window.AlpineBlocks) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
      
      // Timeout after 5 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        resolve();
      }, 5000);
    });
  }

  /**
   * Load content into the RichTextEditor
   */
  async loadContent(content) {
    const contentTextarea = document.getElementById('page_content');
    if (!contentTextarea) {
      console.error('Content textarea not found');
      return;
    }

    const editorDiv = document.getElementById(contentTextarea.id + '-editor');

    try {
      // Load content into both textarea and contenteditable div
      if (typeof content === 'string') {
        contentTextarea.value = content;

        if (editorDiv && editorDiv.contentEditable === 'true') {
          editorDiv.innerHTML = content;
        }
      } else if (content && content.html) {
        // If content is an object with html property
        contentTextarea.value = content.html;

        if (editorDiv && editorDiv.contentEditable === 'true') {
          editorDiv.innerHTML = content.html;
        }
      }

      console.log('Content loaded successfully');
    } catch (error) {
      console.error('Error loading content:', error);
    }
  }

  /**
   * Save the current editor content
   */
  async saveContent() {
    try {
      // Get content from the textarea (automatically synced by RichTextEditor)
      const contentTextarea = document.getElementById('page_content');
      if (!contentTextarea) {
        console.error('Content textarea not found');
        return null;
      }

      // Force a sync rather than relying on the change-driven one: edits made
      // only via the Source code dialog never fire `change`, and the sync is
      // also what swaps shortcode preview blocks back to their raw shortcode.
      window.tinymce?.get('page_content')?.save();

      // Get the contenteditable div to manually sync if needed
      const editorDiv = document.getElementById(contentTextarea.id + '-editor');
      if (editorDiv && editorDiv.contentEditable === 'true') {
        // Ensure content is synced from contenteditable to textarea
        contentTextarea.value = editorDiv.innerHTML;
      }

      // Strip contenteditable attributes before saving
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = contentTextarea.value;
      tempDiv.querySelectorAll('[contenteditable]').forEach(el => {
        el.removeAttribute('contenteditable');
      });
      const htmlContent = tempDiv.innerHTML;

      // Build payload from form fields
      const payload = {
        page: {
          content: htmlContent,
          title: document.getElementById('page_title')?.value || '',
          slug: document.getElementById('page_slug')?.value || '',
          excerpt: document.getElementById('page_excerpt')?.value || '',
          status: document.getElementById('page_status')?.value || 'draft',
          meta_title: document.getElementById('page_meta_title')?.value || '',
          meta_description: document.getElementById('page_meta_description')?.value || '',
          sort_order: document.getElementById('page_sort_order')?.value || 0,
          css_classes: document.getElementById('page_css_classes')?.value || '',
          disable_default_classes: document.getElementById('page_disable_default_classes')?.checked || false,
          page_type: document.querySelector('[data-page-id]')?.dataset.pageType || 'page',
          tag_names: (() => {
            const tagEl = document.querySelector('[x-data*="tagTypeahead"]');
            if (tagEl && window.Alpine) {
              const data = window.Alpine.$data(tagEl);
              if (data?.tags) return data.tags;
            }
            return [];
          })(),
          parent_id: (() => {
            const val = document.getElementById('page_parent_id')?.value;
            return val === '' ? null : val;
          })()
        }
      };

      console.log('Saving page with payload:', payload);

      // Send to server
      const response = await this.sendToServer(payload);

      if (response.ok) {
        this.isDirty = false;
        this.showSuccessMessage();
        return response._parsedData || null;
      } else {
        // Handle validation errors
        const errorData = await response.json();
        const errors = errorData.errors || ['Failed to save content'];
        throw new Error(errors);
      }

    } catch (error) {
      console.error('Error saving content:', error);
      // Check if error.message is an array (validation errors) or string
      const errors = Array.isArray(error.message) ? error.message : [error.message];
      this.showErrorMessage(errors[0], errors);
      return null;
    }
  }

  /**
   * Sync visible form fields to hidden Rails form fields
   */
  syncFormFields() {
    // Map of visible field IDs to hidden field IDs
    const fieldMapping = {
      'page_title': 'form_title',
      'page_slug': 'form_slug',
      'page_excerpt': 'form_excerpt',
      'page_status': 'form_status',
      'page_meta_title': 'form_meta_title',
      'page_meta_description': 'form_meta_description',
      'page_sort_order': 'form_sort_order',
      'page_parent_id': 'form_parent_id'
    };

    // Sync each field
    Object.entries(fieldMapping).forEach(([visibleId, hiddenId]) => {
      const visibleField = document.getElementById(visibleId);
      const hiddenField = document.getElementById(hiddenId);
      
      if (visibleField && hiddenField) {
        hiddenField.value = visibleField.value;
      }
    });
  }

  /**
   * Send content to server
   */
  async sendToServer(payload) {
    const pageId = this.currentPageId || document.querySelector('[data-page-id]')?.dataset.pageId;
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    // Determine if this is a new page (create) or existing page (update)
    const isNewPage = !pageId || pageId === '';

    let url, method;
    if (isNewPage) {
      url = '/admin/pages/pages';
      method = 'POST';
    } else {
      url = `/admin/pages/pages/${pageId}`;
      method = 'PATCH';
    }

    const response = await fetch(url, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    // If this was a successful create, redirect to the edit page
    if (isNewPage && response.ok) {
      const data = await response.json();
      response._parsedData = data;
      if (data.slug || data.id) {
        window.location.href = `/admin/pages/pages/${data.slug || data.id}/edit`;
      }
    }

    return response;
  }

  /**
   * Get Alpine component from element
   */
  getAlpineComponent(element) {
    if (element._x_dataStack && element._x_dataStack[0]) {
      return element._x_dataStack[0];
    }
    
    // Try Alpine.$data as fallback
    if (window.Alpine && window.Alpine.$data) {
      return window.Alpine.$data(element);
    }
    
    return null;
  }

  /**
   * Set up autosave functionality
   */
  setupAutosave() {
    let autosaveTimer = null;
    
    // Listen for changes
    document.addEventListener('alpine:updated', () => {
      this.isDirty = true;
      
      // Clear existing timer
      if (autosaveTimer) {
        clearTimeout(autosaveTimer);
      }
      
      // Set new timer for 30 seconds
      autosaveTimer = setTimeout(() => {
        if (this.isDirty) {
          this.saveContent();
        }
      }, 30000);
    });
  }

  /**
   * Show success message
   */
  showSuccessMessage() {
    const saveButton = document.querySelector('[data-save-button]');
    if (saveButton) {
      const originalText = saveButton.innerHTML;
      saveButton.innerHTML = `
        <svg class="-ml-0.5 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
        Saved!
      `;
      saveButton.classList.add('bg-green-600');
      
      setTimeout(() => {
        saveButton.innerHTML = originalText;
        saveButton.classList.remove('bg-green-600');
      }, 2000);
    }
  }

  /**
   * Show error message
   */
  showErrorMessage(message, errors = null) {
    // Update save button
    const saveButton = document.querySelector('[data-save-button]');
    if (saveButton) {
      const originalText = saveButton.innerHTML;
      saveButton.innerHTML = `
        <svg class="-ml-0.5 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
        Error!
      `;
      saveButton.classList.add('bg-red-600');

      setTimeout(() => {
        saveButton.innerHTML = originalText;
        saveButton.classList.remove('bg-red-600');
      }, 3000);
    }

    // Display validation errors in a better format
    this.displayValidationErrors(errors || [message]);
  }

  /**
   * Display validation errors to the user
   */
  displayValidationErrors(errors) {
    // Remove any existing error alerts
    const existingAlert = document.querySelector('.wps-validation-errors');
    if (existingAlert) {
      existingAlert.remove();
    }

    // Create error alert
    const errorsArray = Array.isArray(errors) ? errors : [errors];

    const alertHtml = `
      <div class="wps-validation-errors bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-800/10 dark:border-red-900 mb-6" role="alert">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800 dark:text-red-400">
              ${errorsArray.length === 1 ? 'There was an error' : `There were ${errorsArray.length} errors`} saving the page:
            </h3>
            <div class="mt-2 text-sm text-red-700 dark:text-red-400">
              <ul class="list-disc list-inside space-y-1">
                ${errorsArray.map(error => `<li>${this.escapeHtml(error)}</li>`).join('')}
              </ul>
            </div>
          </div>
          <div class="ml-auto pl-3">
            <button type="button" class="inline-flex rounded-md p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" onclick="this.closest('.wps-validation-errors').remove()">
              <span class="sr-only">Dismiss</span>
              <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;

    // Insert at the top of the page
    const container = document.querySelector('.flex.flex-col.h-full');
    if (container) {
      const firstChild = container.firstElementChild;
      firstChild.insertAdjacentHTML('afterend', alertHtml);

      // Scroll to the error message
      const errorElement = document.querySelector('.wps-validation-errors');
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Export functions for global use
export function initializeWpsEditor(pageId, content) {
  const editor = new WpsPageEditor();
  editor.initializeEditor(pageId, content);
  
  // Make save function globally available
  window.savePageContent = () => editor.saveContent();
  
  return editor;
}

// Auto-initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  const pageElement = document.querySelector('[data-page-id]');

  if (pageElement) {
    const pageId = pageElement.dataset.pageId;
    const pageContent = pageElement.dataset.pageContent;

    // Set up the save functionality
    const editor = new WpsPageEditor();
    editor.currentPageId = pageId;

    // Load initial content if available
    if (pageContent && pageContent !== '') {
      // Wait a bit for Alpine/AlpineBlocks to initialize
      setTimeout(() => {
        const editorEl = document.querySelector('#alpineblocks-editor');
        if (editorEl && window.loadPageContent) {
          window.loadPageContent(editorEl, pageContent);
        } else {
          console.error('Editor element or loadPageContent not found');
        }
      }, 1000); // Give more time for initialization
    }

    // Make save function globally available
    window.savePageContent = () => editor.saveContent();

    // Initialize new page behavior
    initializeNewPageBehavior(pageId);
  }
});

/**
 * Initialize behavior for new pages
 */
function initializeNewPageBehavior(pageId) {
  // Only run on new page (no pageId)
  if (pageId && pageId !== '') return;

  const titleInput = document.getElementById('page_title');
  const slugInput = document.getElementById('page_slug');

  if (!titleInput) return;

  // Set default placeholder text
  if (!titleInput.value || titleInput.value === '') {
    titleInput.placeholder = 'Page Title';
  }

  // Auto-focus on the title field
  setTimeout(() => {
    titleInput.focus();
  }, 100);

  // Auto-generate slug from title as user types
  titleInput.addEventListener('input', (e) => {
    const title = e.target.value;

    // Only auto-generate if slug is empty or was auto-generated before
    if (!slugInput.dataset.manuallyEdited) {
      const generatedSlug = generateSlug(title);
      slugInput.value = generatedSlug;
    }
  });

  // Mark slug as manually edited if user types in it
  if (slugInput) {
    slugInput.addEventListener('input', () => {
      if (slugInput.value !== '') {
        slugInput.dataset.manuallyEdited = 'true';
      }
    });

    // Also validate slug format as user types
    slugInput.addEventListener('input', (e) => {
      const value = e.target.value;
      const validSlug = value.toLowerCase().replace(/[^a-z0-9\-_]/g, '-');

      if (value !== validSlug) {
        e.target.value = validSlug;
      }
    });
  }
}

/**
 * Generate a URL-friendly slug from text
 */
function generateSlug(text) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove non-word chars except spaces and hyphens
    .replace(/[\s_]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}